import { useState, type Dispatch, type SetStateAction } from 'react';
import type { ResolvedPortfolioGovernancePolicy } from '@einhaus/project314-types';
import { buildDefaultGovernanceDraft, buildDraftFromPolicy, type GovernanceDraft } from './governanceHelpers';

interface GovernanceDraftState {
    isEnabled: boolean;
    setIsEnabled: Dispatch<SetStateAction<boolean>>;
    draft: GovernanceDraft;
    setDraft: Dispatch<SetStateAction<GovernanceDraft>>;
}

/**
 * Holds the editable governance draft and the enabled flag, re-seeding from the loaded policy once per
 * distinct policy state — keyed by policy id, or 'none' when governance is inactive — so background refetches
 * returning the same policy never clobber edits in progress, while a freshly saved revision (new id) or a
 * deactivation re-seeds the form exactly once. State is adjusted during render (guarded by the synced key),
 * which is React's recommended alternative to a server-sync effect.
 */
export const useGovernanceDraftState = (
    isLoaded: boolean,
    activePolicy: ResolvedPortfolioGovernancePolicy | null
): GovernanceDraftState => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [draft, setDraft] = useState<GovernanceDraft>(buildDefaultGovernanceDraft);
    const [syncedKey, setSyncedKey] = useState<number | 'none' | null>(null);

    if (isLoaded) {
        const key = activePolicy ? activePolicy.policyId : 'none';

        if (key !== syncedKey) {
            setSyncedKey(key);
            setIsEnabled(activePolicy !== null);
            setDraft(activePolicy ? buildDraftFromPolicy(activePolicy) : buildDefaultGovernanceDraft());
        }
    }

    return { isEnabled, setIsEnabled, draft, setDraft };
};
